import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { yerelHaftaBasi, yerelGunBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Cip,
  DegradeBaslikHucresi,
  HAFTA_GUNLERI,
  Ikon,
  Iskelet,
  SatirEylemi,
  Secim as UiSecim,
  anMetni,
  cn,
  gunAraligiMetni,
  gunMetni,
  saatMetni,
} from "@koclukpanel/ui";
import { izinVarMi } from "./izin";
import {
  ApiHatasi,
  benGetir,
  type Ben,
  agactakiKonular,
  dersHavuzuGetir,
  gorevEkle,
  gorevGuncelle,
  gorevSil,
  gorevTasi,
  oncekiHaftadanKopyala,
  programDurumDegistir,
  programGetir,
  kayitOnayTalepleri,
  ogrencininMesguliyeti,
  onayTalebiAc,
  programOlustur,
  programRevizyonlari,
  type HaftalikProgram,
  type ProgramGorevi,
} from "./api";
import { ProgramAraclari, type HazirlamaAraci } from "./ProgramAraclari";
import { GorevIcerikAlanlari } from "./GorevIcerikAlanlari";
import {
  BOS_ICERIK,
  icerikFarki,
  icerikGorevden,
  icerikGovdesi,
  icerikHatalari,
  type IcerikDegeri,
} from "./gorev-icerigi";
import { gunDegistir, siraDegistir, type TasinabilirGorev } from "./gorev-tasima";
import { GorevRenkRozeti, ProgramBaglantisi } from "../../ogrenci/src/HaftalikProgramGorunumu";
import { SayfaBasligiEki } from "./Kabuk";
import { ProgramOgrenciGozu } from "./ProgramOgrenciGozu";
import { noktaliTarih } from "./program-durum";
import {
  GorevDamgalari,
  GunRenkOzeti,
  OgrenciGorevVerisi,
  SonucDuzeltFormu,
  goreviAltinaKopyala,
  useKompaktProgram,
} from "./ProgramGorunumAraclari";

const GOREV_TURLERI = [
  { deger: "QUESTION_SOLVING", etiket: "Soru çözümü", konuZorunlu: true },
  { deger: "TOPIC_STUDY", etiket: "Konu çalışması", konuZorunlu: true },
  { deger: "REVIEW", etiket: "Tekrar", konuZorunlu: false },
  { deger: "MOCK_EXAM", etiket: "Deneme", konuZorunlu: false },
  { deger: "VIDEO", etiket: "Video", konuZorunlu: true },
  { deger: "READING", etiket: "Okuma", konuZorunlu: true },
] as const;

const DURUM_ETIKETI: Record<string, string> = {
  PLANNED: "Planlandı",
  STARTED: "Başlandı",
  PENDING: "Bekliyor",
  PARTIAL: "Kısmen",
  COMPLETED: "Tamamlandı",
  POSTPONED: "Ertelendi",
  FAILED: "Yapılamadı",
  AWAITING_REVIEW: "Kontrol bekliyor",
};

/**
 * ISO hafta başı — **yerel** gün sınırıyla.
 *
 * ================================================================
 * ESKİ HÂLİ GECE 00:00–02:59 ARASI BİR HAFTA GERİ ATIYORDU
 * ================================================================
 * Önceki sürüm `t.getUTCDate()` kullanıyordu ve üstündeki yorumda
 * "sunucudakiyle AYNI mantık (tamamen UTC)" yazıyordu. O cümle
 * doğruydu — ve **sorunun kendisiydi**: iki taraf aynı yanlış kuralı
 * kopyaladığı için aynı yanlış cevapta anlaşıyorlardı, dolayısıyla
 * ekran bozuk görünmüyordu.
 *
 * Türkiye UTC+3. Pazartesi yerel 00:30'da UTC hâlâ Pazar; ISO hafta
 * hesabı Pazar'ı önceki haftaya bağladığı için öğrenci **geçen
 * haftanın programını** görüyordu.
 *
 * Kural artık `@koclukpanel/contracts` içinde ve sunucu da oradan
 * okuyor — kopyalanan kural, kopyalanan hatadır.
 */
function haftaBasi(t: Date): string {
  return yerelHaftaBasi(t).toISOString().slice(0, 10);
}

// `saatMetni` artık `@koclukpanel/ui`dan (ortak tarih biçimi, UI/UX Dalga 1 — K7).

function gunEkle(isoGun: string, adet: number): string {
  const d = new Date(`${isoGun}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + adet);
  return d.toISOString().slice(0, 10);
}

interface ProgramProps {
  ogrenciId: string;
  ogrenciAdi: string;
  onGeri: () => void;
  /**
   * Ogrencinin alani secilmemisse gorev formu bu ekrana yonlendiriyor
   * (Ogrenci 360° → Genel Bakis → "Hedefi düzenle"). Verilmezse —
   * kullanici 360°'i goremiyorsa — yalnizca yol yaziliyor, dugme yok:
   * acilamayacak bir ekrana goturen dugme yanlis bir vaat olurdu.
   */
  onOgrenciDosyasi?: () => void;
}

/**
 * HAFTALIK PROGRAM EDITORU — F-SCH
 *
 * Gun x gorev satiri yapisi (F-SCH-02). Gun basina serbest sayida gorev.
 *
 * ================================================================
 * SURE TAHMINI VE YOGUNLUK UYARISI YOK — F-SCH-10
 * ================================================================
 * Ekranda "bu gun cok yogun" turu hicbir uyari yok ve olmayacak.
 * Sistem goreve sure atamiyor; yogunluk kararini uzman ve ogrenci
 * birlikte veriyor. Bir "toplam sure" gostergesi eklemek, olmayan bir
 * olcumu varmis gibi gostermek olurdu.
 */
/**
 * Ad -> bas harfler. "Deniz Yilmaz" -> "DY"
 *
 * Tek kelimelik adda tek harf donuyor; iki harfe TAMAMLAMIYOR, cunku
 * ikinci harfi soyadindan uydurmak yanlis bir kisaltma uretir.
 */
function basHarfler(ad: string): string {
  return ad
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toLocaleUpperCase("tr-TR"))
    .join("");
}

export function Program({ ogrenciId, ogrenciAdi, onGeri, onOgrenciDosyasi }: ProgramProps) {
  const { kompakt, degistir: kompaktDegistir } = useKompaktProgram();
  /*
    İKİ HÂL, TEK EKRAN (sürüm 9a): eski panelde "Haftalık Program Düzenle" ve
    "öğrenci gibi gör" iki ayrı sayfaydı. Burada aynı haftanın iki görünümü;
    veri bir kez okunuyor ve öğrenci hâline geçmek hiçbir istek atmıyor.
  */
  const [mod, setMod] = useState<"duzenle" | "ogrenci">("duzenle");
  const [hafta, setHaftaDurumu] = useState(() => haftaBasi(new Date()));
  const [secilenGun, setSecilenGun] = useState<string | null>(null);
  // F-SCH-04b — "Seçili satıra uygula" için işaretlenen görevler.
  const [secili, setSecili] = useState<ReadonlySet<string>>(() => new Set());
  /*
    Açık hazırlama aracı TEK YERDE; tablodaki onay kutuları buna bakıyor.
    Varsayılan "toplu": eski panelde Hızlı Giriş kartı hep açıktı (EditSchedule).
  */
  const [acikArac, setAcikArac] = useState<HazirlamaAraci | null>("toplu");

  /*
    HAFTA DEĞİŞİNCE ARAÇ VE SEÇİM SIFIRLANIYOR (bağımsız inceleme,
    05.10.2026). Bir haftanın seçimi öbür haftaya taşınmamalı: araç
    varsayılanına döner, seçim boşalır; hızlı giriş formu `key={data.id}`
    ile yeniden kurulduğu için eski haftanın değerleri de kalmaz.
  */
  const setHafta = (yeni: string): void => {
    setHaftaDurumu(yeni);
    setAcikArac("toplu");
    setSecili(new Set());
  };
  const aracAc = (arac: HazirlamaAraci | null): void => {
    setAcikArac(arac);
    // Kapanınca seçim siliniyor: görünmez kalan bir seçim, bir sonraki
    // açılışta koçun hatırlamadığı satırlara yazılırdı.
    if (arac !== "toplu") setSecili(new Set());
  };
  const qc = useQueryClient();

  const anahtar = ["program", ogrenciId, hafta];
  // A10 — "hafta kapanınca yalnız koç düzeltir": düzeltme düğmesi uç izniyle aynı izne bağlı
  // (`calisma.gorev.isaretle`); kapsam (yalnız kendi öğrencisi) sunucuda.
  const { data: ben } = useQuery<Ben>({ queryKey: ["ben"], queryFn: benGetir });
  const sonucDuzeltebilir = ben !== undefined && izinVarMi(ben.izinler, "calisma.gorev.isaretle");
  const { data, isPending, isError, error } = useQuery<HaftalikProgram | null>({
    queryKey: anahtar,
    queryFn: () => programGetir(ogrenciId, hafta),
  });

  const yenile = (): void => {
    void qc.invalidateQueries({ queryKey: anahtar });
    void qc.invalidateQueries({ queryKey: ["is-listesi"] });
  };

  const olustur = useMutation({
    mutationFn: () => programOlustur(ogrenciId, hafta),
    onSuccess: yenile,
  });

  const durumDegistir = useMutation({
    mutationFn: (durum: string) => programDurumDegistir(data!.id, durum),
    onSuccess: yenile,
  });

  const kopyala = useMutation({
    mutationFn: () => oncekiHaftadanKopyala(data!.id, {}),
    onSuccess: yenile,
  });

  const sil = useMutation({
    mutationFn: (gorevId: string) => gorevSil(gorevId),
    onSuccess: yenile,
  });

  const satirKopyala = useMutation({
    mutationFn: (gorev: ProgramGorevi) =>
      goreviAltinaKopyala(
        data!.id,
        gorev,
        data!.items.filter((g) => g.itemDate.slice(0, 10) === gorev.itemDate.slice(0, 10)),
      ),
    // Sıralama kısmen başarısız olsa da oluşturulan kopya görünmeli.
    onSettled: yenile,
  });

  /**
   * TASIMA — F-SCH-07.
   *
   * Hesap `gorev-tasima.ts` icinde (saf, testli); burasi yalnizca
   * ureyen cagrilari SIRAYLA gonderiyor. Paralel gonderilseydi takasin
   * iki yarisi ayni anda ayni siraya yazabilir ve sira bozulurdu.
   */
  const tasi = useMutation({
    mutationFn: async (cagrilar: Array<{ gorevId: string; gun: string; sira: number }>) => {
      for (const c of cagrilar) await gorevTasi(c.gorevId, c.gun, c.sira);
    },
    onSuccess: yenile,
  });

  const tasinabilir = (gorev: ProgramGorevi): TasinabilirGorev => ({
    id: gorev.id,
    gun: gorev.itemDate.slice(0, 10),
    sira: gorev.sortOrder,
  });

  const gunler = useMemo(() => Array.from({ length: 7 }, (_, i) => gunEkle(hafta, i)), [hafta]);

  /*
    ================================================================
    MEŞGULİYET — BİLGİ AMAÇLI, ENGEL DEĞİL (F-SCH-11)
    ================================================================
    Şartname "bilgi amaçlı gösterilir" diyor: uzman öğrencinin okul
    saatini GÖRÜR ama sistem o saate görev yazmayı engellemez.
    Engelleseydi, telafi dersi için okul saatine bilerek görev yazmak
    isteyen uzmanın önü kapanırdı.
  */
  const mesguliyet = useQuery({
    queryKey: ["mesguliyet", ogrenciId, hafta],
    queryFn: () =>
      ogrencininMesguliyeti(ogrenciId, {
        baslangic: `${hafta}T00:00:00.000Z`,
        bitis: `${gunEkle(hafta, 7)}T00:00:00.000Z`,
      }),
    retry: false,
  });

  const gunGorevleri = (gun: string): ProgramGorevi[] =>
    (data?.items ?? [])
      .filter((g) => g.itemDate.slice(0, 10) === gun)
      .sort((a, b) => a.sortOrder - b.sortOrder);

  /*
    SEÇİM YALNIZCA EKRANDAKİ GÖREVLERDEN TÜRETİLİYOR. Silinen ya da başka
    haftada kalan bir görevin kimliği sette kalsa bile sunucuya gitmiyor:
    "3 satır seçili" yazıp 4 kimlik göndermek, koçun görmediği bir satırı
    değiştirmek olurdu (sunucu başka programın satırını zaten değiştirmez).
  */
  const seciliIdler = (data?.items ?? []).filter((g) => secili.has(g.id)).map((g) => g.id);
  const secimDegistir = (id: string, isaretli: boolean): void => {
    setSecili((once) => {
      const yeni = new Set(once);
      if (isaretli) yeni.add(id);
      else yeni.delete(id);
      return yeni;
    });
  };

  const harfler = basHarfler(ogrenciAdi);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/*
        BANT EKİ — eski panelde "Haftalık Program Düzenle" bandının sağında
        öğrenci mini kartı duruyordu (ad, hafta aralığı, "Öğrencinin Haftalık
        Planı"). Bant Kabuk'un; ekran yalnız açıklamayı ve kartı portal ediyor.
      */}
      <SayfaBasligiEki
        aciklama={
          mod === "duzenle"
            ? "Öğretmenler için hızlı ve rahat düzenleme alanı"
            : "Öğrencinin panelinde programın görünüşü — salt okunur"
        }
        eylemler={
          <OgrenciMiniKarti
            ad={ogrenciAdi}
            basHarfler={harfler}
            haftaBasi={hafta}
            haftaSonu={gunEkle(hafta, 6)}
            ogrenciModu={mod === "ogrenci"}
            onDegistir={() => setMod(mod === "duzenle" ? "ogrenci" : "duzenle")}
          />
        }
      />

      {/*
        Hafta gezinmesi ve iş listesine dönüş. Eski panelde hafta, Haftalık
        Program listesindeki karttan seçiliyordu; bizde liste ekranı İş Listesi,
        hafta burada değişiyor — bu satır o yüzden eski panelde YOK.
      */}
      {mod === "duzenle" && (
        <div className="flex flex-wrap items-center gap-2">
          <Buton gorunum="sessiz" onClick={onGeri}>
            ← İş listesi
          </Buton>
          <span aria-hidden="true" className="bg-mor-kenar mx-1 hidden h-6 w-px sm:block" />
          <Buton gorunum="ikincil" onClick={() => setHafta(gunEkle(hafta, -7))}>
            ← Önceki hafta
          </Buton>
          {/*
            "2026-10-05 – 2026-10-11" yazıyordu (UI/UX Dalga 1, K7): ISO biçim
            koçun dili değil. Yıl yalnız başka bir yıla geçilince yazılıyor.
          */}
          <span className="text-metin-ikincil text-sm">
            {gunAraligiMetni(hafta, gunEkle(hafta, 6), { yil: "gerekirse" })}
          </span>
          <Buton gorunum="ikincil" onClick={() => setHafta(gunEkle(hafta, 7))}>
            Sonraki hafta →
          </Buton>
          {hafta !== haftaBasi(new Date()) && (
            <Buton gorunum="sessiz" onClick={() => setHafta(haftaBasi(new Date()))}>
              Bu hafta
            </Buton>
          )}
        </div>
      )}

      {isPending && <Iskelet satir={4} />}

      {isError && (
        <BosDurum
          ton="hata"
          baslik="Program alınamadı"
          aciklama={error instanceof ApiHatasi ? error.message : undefined}
        />
      )}

      {!isPending && !isError && mod === "ogrenci" && (
        <ProgramOgrenciGozu
          key={hafta}
          ogrenciAdi={ogrenciAdi}
          basHarfler={harfler}
          gunler={gunler}
          gorevler={data?.items ?? []}
          onOncekiHafta={() => setHafta(gunEkle(hafta, -7))}
          onBuHafta={() => setHafta(haftaBasi(new Date()))}
          onSonrakiHafta={() => setHafta(gunEkle(hafta, 7))}
          onDuzenlemeyeDon={() => setMod("duzenle")}
        />
      )}

      {mod === "duzenle" && !isPending && !isError && !data && (
        <BosDurum
          baslik="Bu haftanın programı yok"
          aciklama="Program açıp görevleri ekleyin. Yayınlamadan önce öğrenci göremez."
          eylem={
            <div className="flex flex-col items-center gap-2">
              <Buton onClick={() => olustur.mutate()} yukleniyor={olustur.isPending}>
                Program aç
              </Buton>

              {/*
                DÜĞME BASILIYOR AMA HİÇBİR ŞEY OLMUYORDU (07.08.2026, canlı):
                yönetici hesabıyla "Program aç"a basınca sunucu 403
                `UZMAN_DEGIL` dönüyor ama ekranda hiçbir şey değişmiyordu.
                Düğme gizlenmedi — izin yöneticide gerçekten var; eksik olan
                sunucunun cümlesini göstermekti.
              */}
              {olustur.isError && (
                <p role="alert" className="text-hata-500 text-sm font-medium">
                  {olustur.error instanceof ApiHatasi
                    ? olustur.error.message
                    : "Program açılamadı."}
                </p>
              )}
            </div>
          }
        />
      )}

      {mod === "duzenle" && data && (
        <>
          <ProgramDurumSeridi
            program={data}
            onKopyala={() => kopyala.mutate()}
            kopyaCalisiyor={kopyala.isPending}
            hata={
              durumDegistir.error instanceof ApiHatasi
                ? durumDegistir.error.message
                : kopyala.error instanceof ApiHatasi
                  ? kopyala.error.message
                  : null
            }
          />

          <ProgramDuzenleyici
            gunler={gunler}
            gunGorevleri={gunGorevleri}
            kompakt={kompakt}
            onKompakt={kompaktDegistir}
            araclar={
              /*
                `key`: başka bir programa (haftaya) geçince hızlı girişin formu,
                seçili günü ve "N görev güncellendi" sonucu ESKİ haftadan
                kalmasın — gün seçicisi eski haftanın tarihini gönderirdi.

                MAZERETLI GUNLER GOREVLERDEN TURETILIYOR — sunucuda gun bazli
                bir alan YOK, `isExcused` her GOREVDE duruyor.
              */
              <ProgramAraclari
                key={data.id}
                programId={data.id}
                haftaBasi={data.weekStart.slice(0, 10)}
                mazeretliGunler={[
                  ...new Set(
                    data.items.filter((g) => g.isExcused).map((g) => g.itemDate.slice(0, 10)),
                  ),
                ]}
                kilitli={data.status === "ARCHIVED"}
                onDegisti={yenile}
                seciliIdler={seciliIdler}
                gorevGunleri={data.items.map((g) => g.itemDate.slice(0, 10))}
                onSecimiTemizle={() => setSecili(new Set())}
                acik={acikArac}
                onAcik={aracAc}
              />
            }
            mesguliyetler={mesguliyet.data ?? []}
            mesguliyetOkunamadi={mesguliyet.isError}
            duzenlenebilir={data.status !== "ARCHIVED"}
            sonucDuzeltebilir={sonucDuzeltebilir && data.status === "PUBLISHED"}
            ogrenciId={ogrenciId}
            secili={secili}
            onSecim={secimDegistir}
            secimAcik={acikArac === "toplu"}
            onEkle={setSecilenGun}
            onSil={(id) => sil.mutate(id)}
            onKopyala={(g) => satirKopyala.mutate(g)}
            kopyalaniyor={satirKopyala.isPending}
            tasiniyor={tasi.isPending}
            onSira={(gorev, yon) => {
              const gun = gorev.itemDate.slice(0, 10);
              const sonuc = siraDegistir(
                tasinabilir(gorev),
                yon,
                gunGorevleri(gun).map(tasinabilir),
              );
              if (sonuc.cagrilar.length > 0) tasi.mutate(sonuc.cagrilar);
            }}
            onGun={(gorev, adim) => {
              const hedef = gunEkle(gorev.itemDate.slice(0, 10), adim);
              const sonuc = gunDegistir(
                tasinabilir(gorev),
                hedef,
                gunler,
                gunGorevleri(hedef).map(tasinabilir),
              );
              if (sonuc.cagrilar.length > 0) tasi.mutate(sonuc.cagrilar);
            }}
            onGuncellendi={yenile}
            hatalar={[
              sil.error instanceof ApiHatasi ? sil.error.message : null,
              tasi.error instanceof ApiHatasi ? tasi.error.message : null,
              satirKopyala.isError
                ? satirKopyala.error instanceof Error
                  ? satirKopyala.error.message
                  : "Görev kopyalanamadı."
                : null,
            ]}
          />

          <YayinCubugu
            program={data}
            onDurum={(d) => durumDegistir.mutate(d)}
            durumCalisiyor={durumDegistir.isPending}
          />

          <OnayTalepleri programId={data.id} ogrenciId={ogrenciId} />

          <Revizyonlar programId={data.id} />

          {secilenGun && (
            <GorevFormu
              programId={data.id}
              ogrenciId={ogrenciId}
              gun={secilenGun}
              onOgrenciDosyasi={onOgrenciDosyasi}
              onKapat={() => setSecilenGun(null)}
              onEklendi={() => {
                setSecilenGun(null);
                yenile();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Bandın sağındaki öğrenci mini kartı (EditSchedule başlığı). Fotoğraf
 * alanı veride YOK; baş harfli daire onun yerini tutuyor.
 */
function OgrenciMiniKarti({
  ad,
  basHarfler,
  haftaBasi: bas,
  haftaSonu,
  ogrenciModu,
  onDegistir,
}: {
  ad: string;
  basHarfler: string;
  haftaBasi: string;
  haftaSonu: string;
  ogrenciModu: boolean;
  onDegistir: () => void;
}) {
  // Eski panel bu kartta günün başındaki sıfırı yazmıyordu ("6.06.2026").
  const kisa = (gun: string): string => noktaliTarih(gun).replace(/^0/, "");
  return (
    // Bandın eylem yuvası `shrink-0`: kart kendi en geniş metni kadar açılıp 390 px'te
    // sayfayı yana taşıyordu. Üst sınır görüntü genişliğinden, metin alt satıra iniyor.
    <div className="bg-notr-0/85 ring-notr-0 flex max-w-[calc(100vw-3.5rem)] items-center gap-3 rounded-2xl px-4 py-3 shadow-(--golge-unk-kart) ring-1">
      <span
        aria-hidden="true"
        className="text-notr-0 grid size-12 shrink-0 place-items-center rounded-full bg-(image:--gradyan-unk-birincil) font-bold"
      >
        {basHarfler}
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <strong className="text-metin-koyu truncate text-[0.9375rem] font-bold">{ad}</strong>
        <span className="text-metin-ikincil text-xs tabular-nums">
          Hafta Başlangıcı: {kisa(bas)} • Hafta Sonu: {kisa(haftaSonu)}
        </span>
        <button
          type="button"
          aria-pressed={ogrenciModu}
          onClick={onDegistir}
          className="min-h-dokunma border-mor-kenar text-metin-koyu hover:bg-mor-50 bg-notr-0 inline-flex items-center gap-1.5 self-start rounded-full border border-dashed px-3 text-xs font-semibold"
        >
          <Ikon ad="gorunum" className="size-3.5" />
          {ogrenciModu ? "Düzenlemeye dön" : "Öğrencinin Haftalık Planı"}
        </button>
      </div>
    </div>
  );
}

/**
 * Programın durumu (taslak/yayında), tamamlama oranı ve "önceki haftayı
 * kopyala". Eski panelde karşılığı yok — orada program kaydedilince doğrudan
 * öğrenciye gidiyordu; bizde taslak → yayın akışı (F-SCH-07) ve revizyon
 * kaydı (F-SCH-14) var ve koç hangi hâlde olduğunu görmeli.
 */
function ProgramDurumSeridi({
  program,
  onKopyala,
  kopyaCalisiyor,
  hata,
}: {
  program: HaftalikProgram;
  onKopyala: () => void;
  kopyaCalisiyor: boolean;
  hata: string | null;
}) {
  const yayinda = program.status === "PUBLISHED";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {/*
          CIP: kontrast kararını tek yerde tutuyor (`text-basarili-500`
          kendi açık tonunda AA'nın altında kalıyordu, 04.09.2026).
        */}
        <Cip ton={yayinda ? "onay" : "notr"}>
          {yayinda ? "Yayında" : program.status === "ARCHIVED" ? "Arşivlendi" : "Taslak"}
        </Cip>
        <span className="text-metin-ikincil text-sm">{program.items.length} görev</span>
        {program.tamamlama.yuzde !== null && (
          <span className="text-metin-ikincil text-sm">
            Tamamlama %{program.tamamlama.yuzde}
            {program.tamamlama.mazeretli > 0 && ` (${program.tamamlama.mazeretli} mazeretli)`}
          </span>
        )}
        {program.preparedWithStudent && (
          <span className="bg-mor-50 text-mor-700 rounded-full px-3 py-1 text-sm">
            Öğrenciyle birlikte hazırlandı
          </span>
        )}
        {program.revision > 1 && (
          <span className="text-metin-ikincil text-sm">{program.revision}. revizyon</span>
        )}
        {!yayinda && program.status !== "ARCHIVED" && (
          <Buton
            gorunum="kesikli"
            className="ml-auto"
            onClick={onKopyala}
            yukleniyor={kopyaCalisiyor}
          >
            Önceki haftayı kopyala
          </Buton>
        )}
      </div>

      {/* F-SCH-14 — yayından sonra her değişiklik REVİZYON üretiyor; koç bunu ÖNCEDEN bilsin. */}
      {program.revizyonGerekir && (
        <p className="rounded-alan bg-uyari-500/10 text-uyari-600 px-3 py-2 text-sm">
          Program yayında. Yapacağınız her değişiklik revizyon kaydına yazılır ve öğrenci görür.
        </p>
      )}

      {hata && (
        <p role="alert" className="rounded-alan bg-hata-500/10 text-hata-700 px-3 py-2 text-sm">
          {hata}
        </p>
      )}
    </div>
  );
}

/**
 * Tablonun altındaki tam genişlik çubuk — eski paneldeki "Programı Kaydet".
 *
 * Bizde satırlar zaten tek tek kaydediliyor; haftanın SON adımı yayınlamak.
 * Çubuk o yüzden "Programı Yayınla" diyor ve yalnız taslakta duruyor.
 *
 * ================================================================
 * YAYINLAMA KISA ÖZETLİ ONAY İSTİYOR — UI/UX Dalga 1 (O11, mutabakat)
 * ================================================================
 * Öğrenciye GÖRÜNEN işlem bir kez sorulur ve soru BİLGİ taşır ("emin
 * misiniz" değil). Yayınlanan program öğrencinin telefonuna düşüyor ve
 * sonrası yalnız revizyonla değişiyor (F-SCH-07).
 */
function YayinCubugu({
  program,
  onDurum,
  durumCalisiyor,
}: {
  program: HaftalikProgram;
  onDurum: (durum: string) => void;
  durumCalisiyor: boolean;
}) {
  const [yayinOnayi, setYayinOnayi] = useState(false);
  if (program.status === "PUBLISHED" || program.status === "ARCHIVED") return null;
  const gunSayisi = new Set(program.items.map((g) => g.itemDate.slice(0, 10))).size;
  const aralik = gunAraligiMetni(program.weekStart, gunEkle(program.weekStart.slice(0, 10), 6), {
    yil: "gerekirse",
  });
  return (
    <div className="flex flex-col gap-3">
      {/*
        Kaydet çubuğu: eski paneldeki gibi AYRI indigo degrade (#7A3EF6 → #5044EE,
        `--gradyan-unk-kaydet`), birincil mor→pembe değil.
      */}
      <button
        type="button"
        onClick={() => setYayinOnayi(true)}
        disabled={yayinOnayi || durumCalisiyor}
        aria-busy={durumCalisiyor || undefined}
        aria-expanded={yayinOnayi}
        className="text-notr-0 enabled:hover:shadow-yukselen focus-visible:outline-marka-600 flex min-h-[3.375rem] w-full items-center justify-center gap-2 rounded-xl bg-(image:--gradyan-unk-kaydet) text-base font-bold shadow-(--golge-unk-birincil) focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-70"
      >
        <Ikon ad="tamam" className="size-5" />
        Programı Yayınla
      </button>

      {yayinOnayi && (
        <div
          role="alertdialog"
          aria-label="Yayınlama onayı"
          onKeyDown={(e) => {
            if (e.key === "Escape") setYayinOnayi(false);
          }}
          className="rounded-alan border-uyari-200 bg-uyari-50 flex flex-col gap-3 border p-3"
        >
          <p className="text-notr-800 text-sm">
            {aralik ?? "Bu hafta"} ·{" "}
            {program.items.length === 0 ? (
              // Boş programı yayınlamak yasak değil ama kazara olabilir: "0 görev, 0 güne
              // dağılmış" okunmuyordu (GCP gözlemi 06.10.2026), boş olduğu açık söyleniyor.
              <strong>programda henüz görev yok</strong>
            ) : (
              <>
                <strong>{program.items.length} görev</strong>, {gunSayisi} güne dağılmış
              </>
            )}
            . Yayınlanınca öğrenci görür; sonraki her değişiklik revizyon kaydına yazılır.
          </p>
          <div className="flex flex-wrap gap-2">
            <Buton
              yukleniyor={durumCalisiyor}
              onClick={() => {
                setYayinOnayi(false);
                onDurum("PUBLISHED");
              }}
            >
              Evet, yayınla
            </Buton>
            <Buton gorunum="sessiz" onClick={() => setYayinOnayi(false)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
    </div>
  );
}

/*
  SATIR EYLEMİ RENKLERİ — UNİKOÇ program tablosu: − kırmızı, + yeşil, takvim
  mavi (tarama-2 §2.9/5). Ölçülen #FB3C5D/#55BA38/#17BAFF için ayrı belirteç
  yok; durum dörtlüsünün nokta renkleri en yakın tanımlı tonlar. İkonlar
  beyaz; ikon "metin" değil arayüz ögesi, eşiği 3:1 (en düşüğü yeşil, 3,22).
*/
const KARE_RENGI: Record<"kirmizi" | "yesil" | "mavi", string> = {
  kirmizi: "bg-eksik-nokta",
  yesil: "bg-tamam-nokta",
  mavi: "bg-planli-nokta",
};

/**
 * Satır içi küçük renkli kare. GÖRÜNEN 30 px (eski panel), DOKUNMA alanı 44 px:
 * `after` katmanı her yönden 7 px büyütüyor — `SatirEylemi` ile aynı yöntem.
 */
function RenkliKare({
  renk,
  etiket,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  renk: keyof typeof KARE_RENGI;
  etiket: string;
}) {
  return (
    <button
      type="button"
      title={etiket}
      {...rest}
      className={cn(
        "text-notr-0 relative inline-grid size-[1.875rem] shrink-0 place-items-center rounded-lg text-base leading-none font-bold",
        "after:absolute after:-inset-[7px] after:content-['']",
        "enabled:hover:shadow-yukselen transition-shadow disabled:cursor-not-allowed disabled:opacity-40",
        "focus-visible:outline-marka-600 focus-visible:outline-2 focus-visible:outline-offset-2",
        KARE_RENGI[renk],
      )}
    >
      <span aria-hidden="true" className="contents">
        {children}
      </span>
      <span className="sr-only">{etiket}</span>
    </button>
  );
}

/** Tablo hücresindeki beyaz "alan" kutusu — eski paneldeki seçim/girdi kutularının görünümü. */
function Kutu({
  children,
  bos,
  className,
}: {
  children: React.ReactNode;
  /** Değer yoksa yer tutucu gibi soluk yazı (eski paneldeki "Soru", "Test", "YouTube"). */
  bos?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "bg-notr-0 border-mor-kenar flex min-h-9 min-w-0 items-center rounded-lg border px-2.5 text-[0.8125rem]",
        bos ? "text-notr-600" : "text-metin-koyu",
        className,
      )}
    >
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

/** Bağlantı varsa kutu bağlantı olur (şema kontrolü `ProgramBaglantisi`nde); yoksa yer tutucu. */
function BaglantiKutusu({ url, etiket }: { url: string | null; etiket: string }) {
  if (!url) return <Kutu bos>{etiket}</Kutu>;
  return (
    <span className="[&>a]:bg-notr-0 [&>a]:border-mor-kenar [&>a]:text-mor-700 flex min-w-0 [&>a]:min-h-9 [&>a]:w-full [&>a]:truncate [&>a]:rounded-lg [&>a]:border [&>a]:px-2.5 [&>a]:text-[0.8125rem] [&>a]:font-medium">
      <BaglantiRozeti url={url} etiket={etiket} />
    </span>
  );
}

const SUTUNLAR = [
  { ad: "Gün", ikon: "takvim", genislik: 8 },
  { ad: "Ders", ikon: "mufredat", genislik: 16 },
  { ad: "Konu", ikon: "hedef", genislik: 12 },
  { ad: "Kaynak Kitap", ikon: "defter", genislik: 13 },
  { ad: "Soru/Test", ikon: "gorevler", genislik: 9 },
  { ad: "Video", ikon: "canli", genislik: 10 },
  { ad: "PDF", ikon: "indir", genislik: 10 },
  { ad: "Not", ikon: "duzenle", genislik: 8 },
  { ad: "İşlem", ikon: "sistem", genislik: 14 },
] as const;

/*
  KOMPAKT MODDA Video, PDF ve Not sütunları ÇIKIYOR (eski panelde "Kompakt
  Mod"un davranışı görüntüden okunamadı — tarama-2 §5). Satır bir bakışta
  okunacak kadar kısa kalıyor; ayrıntılar satır altında açılıyor.
*/
const KOMPAKTTA_YOK: ReadonlySet<string> = new Set(["Video", "PDF", "Not"]);

const ARAC_DUGMESI =
  "min-h-dokunma bg-notr-0 text-metin-ikincil hover:bg-mor-50 inline-flex items-center gap-2 rounded-xl px-4 text-sm font-medium shadow-(--golge-unk-kart)";
const HUCRE = "px-2 py-2 align-top";

/**
 * HAFTALIK PROGRAM TABLOSU — UNİKOÇ `WeeklySchedule-EditSchedule` (sürüm 9a)
 *
 * Tek büyük tablo: gün hücresi o günün bütün satırlarını kapsıyor (rowspan),
 * her günün satırları kendi pastel renginde (`HAFTA_GUNLERI`, Pazartesi'den).
 *
 * ================================================================
 * HÜCRELER SATIR İÇİ DÜZENLENMİYOR — eski panelden bilinçli fark
 * ================================================================
 * Eski panelde her hücre açılır liste/girdi idi ve hafta tek "Kaydet" ile
 * gidiyordu. Bizde görev tek tek kaydediliyor (her değişiklik denetim
 * kaydına ve yayındaysa revizyona yazılıyor — F-SCH-14). Hücreleri canlı
 * girdi yapmak, her değişikliği ayrı bir revizyon yapmak ya da "kaydedilmedi"
 * hâli olan ikinci bir kayıt modeli kurmak demekti. Hücreler eski panelin
 * kutu görünümünde DEĞERİ gösteriyor; değiştirmek satır altında açılan
 * düzenleme formuyla.
 *
 * ================================================================
 * SÜRE TAHMİNİ VE YOĞUNLUK UYARISI YOK — F-SCH-10
 * ================================================================
 * Sistem göreve süre atamıyor; yoğunluk kararını uzman ve öğrenci birlikte
 * veriyor. Bir "toplam süre" göstergesi olmayan bir ölçümü varmış gibi
 * gösterirdi.
 */
function ProgramDuzenleyici({
  gunler,
  gunGorevleri,
  kompakt,
  onKompakt,
  araclar,
  mesguliyetler,
  mesguliyetOkunamadi,
  duzenlenebilir,
  sonucDuzeltebilir,
  ogrenciId,
  secili,
  onSecim,
  secimAcik,
  onEkle,
  onSil,
  onKopyala,
  kopyalaniyor,
  tasiniyor,
  onSira,
  onGun,
  onGuncellendi,
  hatalar,
}: {
  gunler: readonly string[];
  gunGorevleri: (gun: string) => ProgramGorevi[];
  kompakt: boolean;
  onKompakt: () => void;
  araclar: React.ReactNode;
  mesguliyetler: ReadonlyArray<{ baslik: string; baslangic: string; bitis: string }>;
  mesguliyetOkunamadi: boolean;
  duzenlenebilir: boolean;
  /** Yayındaki programda ve `calisma.gorev.isaretle` izninde — koçun sonuç düzeltmesi (A10). */
  sonucDuzeltebilir: boolean;
  ogrenciId: string;
  secili: ReadonlySet<string>;
  onSecim: (gorevId: string, isaretli: boolean) => void;
  /** Onay kutuları yalnızca hızlı giriş açıkken (F-SCH-04b). */
  secimAcik: boolean;
  onEkle: (gun: string) => void;
  onSil: (id: string) => void;
  onKopyala: (gorev: ProgramGorevi) => void;
  kopyalaniyor: boolean;
  tasiniyor: boolean;
  onSira: (gorev: ProgramGorevi, yon: "yukari" | "asagi") => void;
  onGun: (gorev: ProgramGorevi, adim: -1 | 1) => void;
  onGuncellendi: () => void;
  hatalar: ReadonlyArray<string | null>;
}) {
  const kapRef = useRef<HTMLDivElement>(null);
  const [tamEkran, setTamEkran] = useState(false);
  useEffect(() => {
    const dinle = (): void => setTamEkran(document.fullscreenElement === kapRef.current);
    document.addEventListener("fullscreenchange", dinle);
    return () => document.removeEventListener("fullscreenchange", dinle);
  }, []);
  const tamEkranDegistir = (): void => {
    // Tam ekran API'si olmayan tarayıcıda (eski iOS Safari) düğme bir şey
    // yapmıyor; hata fırlatıp ekranı düşürmekten iyi.
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void kapRef.current?.requestFullscreen?.()?.catch(() => undefined);
  };

  // Satır altı paneller TEK YERDE: aynı anda tek satır açık, rowspan hesabı da buradan.
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);
  const [ayrinti, setAyrinti] = useState<string | null>(null);
  const [duzeltilen, setDuzeltilen] = useState<string | null>(null);
  const altSatirAcik = (id: string): boolean =>
    duzenlenen === id || duzeltilen === id || (kompakt && ayrinti === id);

  const sutunlar = SUTUNLAR.filter((s) => !kompakt || !KOMPAKTTA_YOK.has(s.ad));
  const bugun = yerelGunBasi().toISOString().slice(0, 10);
  const gorunurHatalar = hatalar.filter((h): h is string => h !== null);

  return (
    <div
      ref={kapRef}
      className="flex min-w-0 flex-col gap-4 [&:fullscreen]:overflow-auto [&:fullscreen]:bg-(image:--gradyan-unk-zemin) [&:fullscreen]:p-6"
    >
      {/* Araç şeridi: Kompakt Mod · Tam Ekran · gün hızlı-atlama hapları. */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" aria-pressed={kompakt} onClick={onKompakt} className={ARAC_DUGMESI}>
          <span
            aria-hidden="true"
            className={cn(
              "grid size-4 place-items-center rounded border text-[0.625rem]",
              kompakt ? "bg-mor-500 border-mor-500 text-notr-0" : "border-notr-400",
            )}
          >
            {kompakt ? "✓" : ""}
          </span>
          Kompakt Mod
        </button>
        <button
          type="button"
          aria-pressed={tamEkran}
          onClick={tamEkranDegistir}
          className={ARAC_DUGMESI}
        >
          <span aria-hidden="true">⛶</span>
          {tamEkran ? "Tam Ekrandan Çık" : "Tam Ekran"}
        </button>
        <nav aria-label="Güne git" className="flex flex-wrap gap-2">
          {gunler.map((gun, i) => (
            <a
              key={gun}
              href={`#program-gun-${gun}`}
              onClick={(e) => {
                // Hash adres çubuğuna yazılmasın: panel adresi ekran seçimi için kullanıyor.
                e.preventDefault();
                document
                  .getElementById(`program-gun-${gun}`)
                  ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
              }}
              className={cn(
                "min-h-dokunma bg-notr-0 text-metin-koyu hover:bg-mor-50 inline-flex items-center rounded-full px-4 text-[0.9375rem] font-semibold shadow-(--golge-unk-kart)",
                gun === bugun && "ring-mor-500 ring-2",
              )}
            >
              {HAFTA_GUNLERI[i]?.ad}
            </a>
          ))}
        </nav>
      </div>

      {araclar}

      {/*
        MEŞGULİYET OKUNAMADIYSA "MEŞGULİYET YOK" DEĞİL: ölçüm düştüğünde
        günler meşguliyetsiz çiziliyor ve uzman "boşmuş" diye yazıyordu.
        Uyarı tablonun ÜSTÜNDE tek kez — yedi güne yedi kopya gürültü olurdu.
      */}
      {mesguliyetOkunamadi && (
        <p className="rounded-alan border-uyari-200 bg-uyari-50 text-notr-800 border px-3 py-2 text-sm">
          Öğrencinin okul/dershane saatleri <strong>okunamadı</strong>. Aşağıdaki günler
          meşguliyetsiz görünüyor; bu, meşguliyet olmadığı anlamına gelmez.
        </p>
      )}

      {gorunurHatalar.map((h) => (
        <p
          key={h}
          role="alert"
          className="rounded-alan bg-hata-500/10 text-hata-700 px-3 py-2 text-sm"
        >
          {h}
        </p>
      ))}

      {/*
        DAR EKRANDA TABLO YANA KAYIYOR: dokuz sütun ve satır başına on iki
        alan 390 px'e sığmıyor; karta çevirmek gün rengini ve rowspan'i —
        bu ekranın okunuşunu — kaybettirirdi. Kaydırma kabın içinde, sayfa
        yana kaymıyor.
      */}
      <div className="bg-notr-0 border-mor-kenar overflow-x-auto rounded-[1.25rem] border shadow-(--golge-unk-kart)">
        <table
          className={cn(
            "w-full table-fixed border-collapse text-left text-sm",
            kompakt ? "min-w-[46rem]" : "min-w-[66rem]",
          )}
        >
          {/*
            SÜTUN GENİŞLİKLERİ SABİT (`table-fixed`): içerik genişliğine bırakınca
            uzun kitap adı Kaynak sütununu şişirip Video/PDF/İşlem'i ekran dışına
            itiyordu. Oranlar eski panelin 1440 ekranından ölçüldü.
          */}
          <colgroup>
            {sutunlar.map((s) => (
              <col key={s.ad} style={{ width: `${s.genislik}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {sutunlar.map((s) => (
                <DegradeBaslikHucresi key={s.ad} className="text-center whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    <Ikon ad={s.ikon} className="size-4" />
                    {s.ad}
                  </span>
                </DegradeBaslikHucresi>
              ))}
            </tr>
          </thead>
          <tbody>
            {gunler.map((gun, i) => {
              const gunBilgisi = HAFTA_GUNLERI[i];
              const gunAdi = gunBilgisi?.ad ?? "";
              const zemin = gunBilgisi?.zemin;
              const gorevler = gunGorevleri(gun);
              const gunMesguliyetleri = mesguliyetler.filter(
                (m) => m.baslangic.slice(0, 10) === gun,
              );
              const satirSayisi =
                Math.max(gorevler.length, 1) + gorevler.filter((g) => altSatirAcik(g.id)).length;
              // Günler arası kalın lavanta çizgi (eski panel: 2 px #D4C9F1).
              const gunAyraci = "border-t-2 border-mor-kesikli";

              const gunHucresi = (
                <th
                  scope="rowgroup"
                  rowSpan={satirSayisi}
                  className={cn("px-2 py-3 text-center align-middle", zemin)}
                >
                  <span className="text-metin-koyu block text-[0.9375rem] font-bold">{gunAdi}</span>
                  <span className="text-metin-ikincil block text-xs font-normal">
                    {gunMetni(gun)}
                    {gun === bugun && " · Bugün"}
                  </span>
                  {gorevler.length > 0 && (
                    <span className="mt-2 flex justify-center">
                      <GunRenkOzeti gorevler={gorevler} />
                    </span>
                  )}
                  {/*
                    MEŞGULİYET GÖREVLERDEN AYRI ÇİZİLİYOR — soluk, kesik
                    kenarlı. Görev satırına karıştırılsaydı uzman onu silinebilir
                    bir görev sanardı; oysa öğrencinin okul saati, buradan
                    yönetilmiyor.
                  */}
                  {gunMesguliyetleri.length > 0 && (
                    <ul className="mt-2 flex flex-col gap-1 text-left">
                      {gunMesguliyetleri.map((m, j) => (
                        <li
                          key={`${m.baslangic}-${j}`}
                          className="rounded-alan border-notr-400 text-notr-700 bg-notr-0/60 border border-dashed px-2 py-1 text-xs font-normal"
                        >
                          <span className="tabular-nums">
                            {saatMetni(m.baslangic)}–{saatMetni(m.bitis)}
                          </span>{" "}
                          {m.baslik}
                        </li>
                      ))}
                    </ul>
                  )}
                </th>
              );

              if (gorevler.length === 0) {
                return (
                  <tr key={gun} id={`program-gun-${gun}`} className={cn(zemin, gunAyraci)}>
                    {gunHucresi}
                    <td colSpan={sutunlar.length - 1} className="px-3 py-3">
                      <div className="flex flex-wrap items-center gap-3">
                        {/*
                          BOŞ GÜN BİR HATA DEĞİL. Ton `notr-600`: pastel zeminde
                          de AA'yı geçiyor; boş günü okunamaz yazmak onu "yokmuş"
                          gibi göstermek olurdu.
                        */}
                        <p className="text-notr-600 text-sm">Görev yok</p>
                        {duzenlenebilir && (
                          <RenkliKare
                            renk="yesil"
                            etiket={`${gunAdi}: görev ekle`}
                            onClick={() => onEkle(gun)}
                          >
                            +
                          </RenkliKare>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }

              return gorevler.map((g, sira) => {
                const tur = GOREV_TURLERI.find((t) => t.deger === g.kind)?.etiket ?? g.kind;
                return (
                  <Fragment key={g.id}>
                    <tr
                      id={sira === 0 ? `program-gun-${gun}` : undefined}
                      className={cn(
                        zemin,
                        sira === 0 ? gunAyraci : "border-notr-900/5 border-t",
                        secili.has(g.id) && "outline-mor-500 outline-2 -outline-offset-2",
                      )}
                    >
                      {sira === 0 && gunHucresi}

                      {/* DERS — ders adı, durum rozeti, öğrencinin girdiği veri */}
                      <td className={HUCRE}>
                        <div className="flex min-w-0 items-center gap-1">
                          {/*
                            SATIR SEÇİMİ — F-SCH-04b "Seçili satıra uygula".
                            GERÇEK `<input type="checkbox">`: klavyeyle Boşluk ile
                            çalışıyor, ekran okuyucu durumu kendisi duyuruyor.
                            Kutu 16 px, tıklama alanı `<label>` sayesinde 44 × 44.
                          */}
                          {duzenlenebilir && secimAcik && (
                            <label className="min-h-dokunma min-w-dokunma -my-1 -ml-2 flex shrink-0 cursor-pointer items-center justify-center">
                              <input
                                type="checkbox"
                                checked={secili.has(g.id)}
                                onChange={(e) => onSecim(g.id, e.target.checked)}
                                className="accent-mor-500 size-4 cursor-pointer"
                              />
                              <span className="sr-only">
                                Hızlı giriş için seç: {gunAdi}, {g.dersAdi ?? "ders"}
                                {g.konuAdi ? `, ${g.konuAdi}` : ""}
                              </span>
                            </label>
                          )}
                          <Kutu className="flex-1">{g.dersAdi ?? "Ders"}</Kutu>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          <GorevRenkRozeti renk={g.renk} />
                          <GorevDamgalari gorev={g} />
                        </div>
                        {/*
                          Öğrenci ölçümü YALNIZ girilmişse: henüz gelmemiş günde dört
                          kez "veri girilmedi" yazmak satırı iki katına çıkarıyordu;
                          durum rozeti ("Bekliyor"/"Veri girilmedi") bunu zaten söylüyor.
                        */}
                        {(g.correctCount != null ||
                          g.wrongCount != null ||
                          g.emptyCount != null ||
                          g.durationSeconds != null) && (
                          <div className="mt-1">
                            <OgrenciGorevVerisi gorev={g} />
                          </div>
                        )}
                        {sonucDuzeltebilir && duzeltilen !== g.id && (
                          <button
                            type="button"
                            onClick={() => setDuzeltilen(g.id)}
                            className="text-mor-700 min-h-dokunma text-xs font-semibold underline-offset-2 hover:underline"
                          >
                            Sonucu düzelt
                          </button>
                        )}
                      </td>

                      {/* KONU — altında görev türü, saat ve görev durumu (eski panelde yok) */}
                      <td className={HUCRE}>
                        <Kutu bos={!g.konuAdi}>{g.konuAdi ?? "Konu yok"}</Kutu>
                        <span className="text-metin-ikincil mt-1 block text-xs">
                          {tur}
                          {g.startTime ? ` · ${g.startTime}` : ""} ·{" "}
                          {DURUM_ETIKETI[g.status] ?? g.status}
                        </span>
                      </td>

                      <td className={HUCRE}>
                        <Kutu bos={!g.kaynakAdi}>{g.kaynakAdi ?? "Kaynak yok"}</Kutu>
                      </td>

                      <td className={HUCRE}>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Kutu bos={g.targetQuestionCount === null} className="tabular-nums">
                            {g.targetQuestionCount ?? "Soru"}
                          </Kutu>
                          <Kutu bos={g.targetTestCount === null} className="tabular-nums">
                            {g.targetTestCount ?? "Test"}
                          </Kutu>
                        </div>
                      </td>

                      {!kompakt && (
                        <>
                          <td className={HUCRE}>
                            <div className="grid grid-cols-2 gap-1.5">
                              <BaglantiKutusu url={g.youtubeUrl} etiket="YouTube" />
                              <Kutu bos={!g.videoTitle}>{g.videoTitle ?? "Video"}</Kutu>
                            </div>
                          </td>
                          <td className={HUCRE}>
                            <div className="grid grid-cols-2 gap-1.5">
                              <BaglantiKutusu url={g.topicPdfUrl} etiket="Konu PDF" />
                              <BaglantiKutusu url={g.testPdfUrl} etiket="Test PDF" />
                            </div>
                          </td>
                          <td className={cn(HUCRE, "text-center")}>
                            {duzenlenebilir ? (
                              <button
                                type="button"
                                title={g.coachNote ?? undefined}
                                aria-expanded={duzenlenen === g.id}
                                onClick={() => setDuzenlenen(duzenlenen === g.id ? null : g.id)}
                                className="bg-planli-zemin text-planli-metin ring-planli-nokta/30 hover:ring-planli-nokta min-h-9 rounded-lg px-3 text-[0.8125rem] font-medium whitespace-nowrap ring-1"
                              >
                                {g.coachNote ? "Not Var" : "Not Ekle"}
                              </button>
                            ) : (
                              g.coachNote && (
                                <span className="text-metin-ikincil line-clamp-2 text-xs">
                                  {g.coachNote}
                                </span>
                              )
                            )}
                          </td>
                        </>
                      )}

                      {/* İŞLEM — − sil · + ekle · takvim (altına kopyala) · düzenle */}
                      <td className={HUCRE}>
                        {duzenlenebilir && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <RenkliKare
                              renk="kirmizi"
                              etiket="Görevi sil"
                              onClick={() => onSil(g.id)}
                            >
                              −
                            </RenkliKare>
                            <RenkliKare
                              renk="yesil"
                              etiket={`${gunAdi}: görev ekle`}
                              onClick={() => onEkle(gun)}
                            >
                              +
                            </RenkliKare>
                            <RenkliKare
                              renk="mavi"
                              etiket="Altına kopyala"
                              disabled={kopyalaniyor || tasiniyor}
                              onClick={() => onKopyala(g)}
                            >
                              <Ikon ad="takvim" className="size-4" />
                            </RenkliKare>
                            {kompakt ? (
                              <Buton
                                gorunum="sessiz"
                                aria-expanded={ayrinti === g.id}
                                onClick={() => setAyrinti(ayrinti === g.id ? null : g.id)}
                              >
                                Ayrıntılar
                              </Buton>
                            ) : (
                              <SatirEylemi
                                tur="duzenle"
                                etiket="Düzenle"
                                aria-expanded={duzenlenen === g.id}
                                onClick={() => setDuzenlenen(duzenlenen === g.id ? null : g.id)}
                                className="size-[1.875rem]"
                              />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>

                    {altSatirAcik(g.id) && (
                      <tr className={zemin}>
                        <td colSpan={sutunlar.length - 1} className="px-3 pb-3">
                          <div className="bg-notr-0 border-mor-kenar flex flex-col gap-3 rounded-xl border p-3">
                            {kompakt && ayrinti === g.id && (
                              <div className="flex flex-wrap items-center gap-2">
                                <BaglantiRozeti url={g.youtubeUrl} etiket="Video" />
                                <BaglantiRozeti url={g.topicPdfUrl} etiket="Konu PDF" />
                                <BaglantiRozeti url={g.testPdfUrl} etiket="Test PDF" />
                                {g.coachNote && (
                                  <span className="text-metin-ikincil text-xs whitespace-pre-wrap">
                                    Not: {g.coachNote}
                                  </span>
                                )}
                                {duzenlenebilir && (
                                  <Buton
                                    gorunum="kesikli"
                                    aria-expanded={duzenlenen === g.id}
                                    onClick={() => setDuzenlenen(duzenlenen === g.id ? null : g.id)}
                                  >
                                    Düzenle
                                  </Buton>
                                )}
                              </div>
                            )}
                            {duzenlenebilir && duzenlenen === g.id && (
                              <>
                                <TasimaDugmeleri
                                  gorev={g}
                                  ilk={sira === 0}
                                  son={sira === gorevler.length - 1}
                                  haftaninIlkGunu={i === 0}
                                  haftaninSonGunu={i === 6}
                                  tasiniyor={tasiniyor}
                                  onSira={onSira}
                                  onGun={onGun}
                                />
                                <GorevDuzenle
                                  gorev={g}
                                  ogrenciId={ogrenciId}
                                  onBitti={() => {
                                    setDuzenlenen(null);
                                    onGuncellendi();
                                  }}
                                />
                              </>
                            )}
                            {sonucDuzeltebilir && duzeltilen === g.id && (
                              <SonucDuzeltFormu
                                gorev={g}
                                onBitti={() => {
                                  setDuzeltilen(null);
                                  onGuncellendi();
                                }}
                                onVazgec={() => setDuzeltilen(null)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * TAŞIMA DÜĞMEYLE, SÜRÜKLEYEREK DEĞİL — F-SCH-07
 *
 * Sistem mobil öncelikli. Sürükle-bırak telefonda tek elle kullanılamıyor
 * ve ekran okuyucuyla hiç erişilemiyor. Dört düğme ikisinde de çalışıyor.
 * Etiketler `sr-only` ile YAZILI: yalnız ok işareti "düğme" diye okunurdu.
 */
function TasimaDugmeleri({
  gorev,
  ilk,
  son,
  haftaninIlkGunu,
  haftaninSonGunu,
  tasiniyor,
  onSira,
  onGun,
}: {
  gorev: ProgramGorevi;
  ilk: boolean;
  son: boolean;
  haftaninIlkGunu: boolean;
  haftaninSonGunu: boolean;
  tasiniyor: boolean;
  onSira: (gorev: ProgramGorevi, yon: "yukari" | "asagi") => void;
  onGun: (gorev: ProgramGorevi, adim: -1 | 1) => void;
}) {
  const sinif =
    "min-h-dokunma min-w-dokunma rounded-alan text-metin-ikincil hover:bg-mor-50 px-2 text-sm disabled:opacity-30";
  return (
    <div className="flex flex-wrap items-center gap-0.5">
      <span className="text-metin-ikincil mr-1 text-xs">Taşı:</span>
      {(
        [
          ["yukari", "↑", "Yukarı taşı", ilk],
          ["asagi", "↓", "Aşağı taşı", son],
        ] as const
      ).map(([yon, simge, etiket, kapali]) => (
        <button
          key={yon}
          type="button"
          disabled={kapali || tasiniyor}
          onClick={() => onSira(gorev, yon)}
          className={sinif}
        >
          <span className="sr-only">{etiket}</span>
          <span aria-hidden="true">{simge}</span>
        </button>
      ))}
      {(
        [
          [-1, "◀", "Bir gün öncesine taşı", haftaninIlkGunu],
          [1, "▶", "Bir gün sonrasına taşı", haftaninSonGunu],
        ] as const
      ).map(([adim, simge, etiket, kapali]) => (
        <button
          key={adim}
          type="button"
          disabled={kapali || tasiniyor}
          onClick={() => onGun(gorev, adim)}
          className={sinif}
        >
          <span className="sr-only">{etiket}</span>
          <span aria-hidden="true">{simge}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Gorev formu.
 *
 * Konu alani gorev turune gore ZORUNLU olup olmadigini degistiriyor
 * (F-SCH-03). Sunucu zaten dogruluyor; buradaki isaret yalnizca
 * kullaniciyi bosuna gonderim yapmaktan kurtariyor.
 */
function GorevFormu({
  programId,
  ogrenciId,
  gun,
  onOgrenciDosyasi,
  onKapat,
  onEklendi,
}: {
  programId: string;
  ogrenciId: string;
  gun: string;
  onOgrenciDosyasi?: () => void;
  onKapat: () => void;
  onEklendi: () => void;
}) {
  const [tur, setTur] = useState<string>("QUESTION_SOLVING");
  const [dersId, setDersId] = useState("");
  const [konuId, setKonuId] = useState("");
  const [hedef, setHedef] = useState("");
  const [saat, setSaat] = useState("");
  const [not, setNot] = useState("");
  const [icerik, setIcerik] = useState<IcerikDegeri>(BOS_ICERIK);

  /*
    ================================================================
    ALAN SEÇİMİ KALKTI — F-SCH-03b (04.10.2026, müşteri revizesi)
    ================================================================
    Koç her görevde önce "Alan" seçiyordu; Sayısal bir öğrenciye TYT
    Türkçe yazmak için alanı değiştirmesi, sonra AYT Fizik için geri
    çevirmesi gerekiyordu. Liste artık öğrencinin alanından sunucuda
    kuruluyor (AYT alanında TYT de içinde) ve adlar önekli geliyor.
  */
  const havuz = useQuery({
    queryKey: ["ders-havuzu", ogrenciId],
    queryFn: () => dersHavuzuGetir(ogrenciId),
    retry: false,
  });

  const icerikHata = icerikHatalari(icerik);
  const icerikGecerli = Object.keys(icerikHata).length === 0;

  const ekle = useMutation({
    mutationFn: () =>
      gorevEkle(programId, {
        itemDate: gun,
        kind: tur,
        lessonId: dersId,
        topicId: konuId || null,
        targetQuestionCount: hedef ? Number(hedef) : null,
        startTime: saat || null,
        coachNote: not || null,
        ...icerikGovdesi(icerik),
      }),
    onSuccess: onEklendi,
  });

  const konuZorunlu = GOREV_TURLERI.find((t) => t.deger === tur)?.konuZorunlu ?? true;
  /*
    ================================================================
    ÜNİTE KONU DEĞİLDİR — 07.09.2026
    ================================================================
    Müfredatta ünite varsa dersin çocukları ÜNİTEDİR; konular onların
    altında. Canlı denemede "Konu (zorunlu)" listesi ünite adlarını
    gösterdi, seçilince sunucu 400 döndü. `agactakiKonular` ağacı
    düzleştirip yalnızca `tur === "konu"` olanları veriyor.
  */
  const tumDersler = (havuz.data?.gruplar ?? []).flatMap((g) => g.dersler);
  const secilenDers = tumDersler.find((d) => d.id === dersId);
  const konular = secilenDers ? agactakiKonular(secilenDers.cocuklar ?? []) : [];

  function gonder(e: FormEvent): void {
    e.preventDefault();
    if (!icerikGecerli) return;
    ekle.mutate();
  }

  const alan = havuz.data?.ogrenciAlani ?? null;

  return (
    <div className="bg-notr-900/40 fixed inset-0 z-50 flex items-end justify-center p-0 md:items-center md:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Görev ekle"
        className="rounded-t-kart bg-notr-0 md:rounded-kart max-h-[92vh] w-full max-w-2xl overflow-y-auto p-4 md:p-6"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Görev ekle — {gunMetni(gun)}</h2>
          <Buton gorunum="sessiz" onClick={onKapat}>
            Kapat
          </Buton>
        </div>

        {ekle.error instanceof ApiHatasi && (
          <p
            role="alert"
            className="rounded-alan bg-hata-500/10 text-hata-700 mb-3 px-3 py-2 text-sm"
          >
            {ekle.error.message}
          </p>
        )}

        {/*
          BOŞ LİSTENİN ÜÇ AYRI SEBEBİ VAR ve üçü ayrı söyleniyor: sorgu
          düştü / öğrencinin alanı yok / alanı kullanımda değil. Tek bir
          "ders yok" cümlesi koçu Müfredat ekranına gönderirdi; oysa çoğu
          zaman eksik olan öğrencinin kaydındaki alan.
        */}
        {havuz.isError ? (
          <p className="rounded-alan border-uyari-200 bg-uyari-50 text-notr-800 mb-3 border px-3 py-2 text-sm">
            Ders listesi <strong>okunamadı</strong>. Bu, müfredat tanımlı olmadığı anlamına gelmez.
          </p>
        ) : havuz.isSuccess && alan === null ? (
          <p className="rounded-alan border-uyari-200 bg-uyari-50 text-notr-800 mb-3 border px-3 py-2 text-sm">
            {/*
              UI/UX Dalga 1 (mutabakat K5/F03): metin "Öğrenci dosyası" diye
              var olmayan bir ekranı gösteriyordu; hedef formu Öğrenci 360°
              icinde. Koç yolu ezberlemek yerine tek dokunusla oraya gider —
              secili ogrenci korunuyor (OGRENCI_EKRANLARI).
            */}
            Öğrencinin <strong>alanı seçilmemiş</strong>; ders listesi alana göre gelir. Alanı
            Öğrenci 360° → Genel Bakış → “Hedefi düzenle” bölümünden seçin.
            {onOgrenciDosyasi && (
              <span className="mt-2 block">
                <Buton gorunum="ikincil" onClick={onOgrenciDosyasi}>
                  Öğrenci 360°'ı aç
                </Buton>
              </span>
            )}
          </p>
        ) : havuz.isSuccess && alan !== null && !alan.kullanilabilir ? (
          <p className="rounded-alan border-uyari-200 bg-uyari-50 text-notr-800 mb-3 border px-3 py-2 text-sm">
            Öğrencinin alanı (<strong>{alan.ad}</strong>) kullanımda değil. Öğrenciye etkin bir alan
            seçin ya da alanı Müfredat ekranından etkinleştirin.
          </p>
        ) : null}

        <form onSubmit={gonder} className="flex flex-col gap-6">
          <fieldset className="flex min-w-0 flex-col gap-4">
            <legend className="text-notr-900 mb-2 text-sm font-semibold">
              Ders ve konu{alan?.kullanilabilir ? ` · ${alan.ad}` : ""}
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Secim etiket="Görev türü" deger={tur} onChange={setTur}>
                {GOREV_TURLERI.map((t) => (
                  <option key={t.deger} value={t.deger}>
                    {t.etiket}
                  </option>
                ))}
              </Secim>

              <Secim
                etiket="Ders"
                deger={dersId}
                onChange={(v) => {
                  setDersId(v);
                  setKonuId("");
                  // Kaynak derse bağlı: ders değişince eski seçim anlamsız.
                  setIcerik((d) => ({ ...d, kaynakId: "" }));
                }}
                devreDisi={!havuz.isSuccess || tumDersler.length === 0}
              >
                <option value="">Seçin…</option>
                {(havuz.data?.gruplar ?? []).map((g) => (
                  <optgroup key={g.alan.id} label={g.alan.ad}>
                    {g.dersler.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.ad}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Secim>
            </div>

            <Secim
              etiket={konuZorunlu ? "Konu (zorunlu)" : "Konu (bu türde boş bırakılabilir)"}
              deger={konuId}
              onChange={setKonuId}
              devreDisi={!dersId}
            >
              <option value="">{konuZorunlu ? "Seçin…" : "Konu yok"}</option>
              {konular.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.ad}
                </option>
              ))}
            </Secim>

            <div className="grid gap-4 sm:grid-cols-2">
              <Alan
                etiket="Hedef soru sayısı"
                type="number"
                min={1}
                value={hedef}
                onChange={(e) => setHedef(e.target.value)}
                yardim="İsteğe bağlı. Öğrenci bu hedefe ulaşmadan görev tamamlandı sayılmaz."
              />
              <Alan
                etiket="Başlangıç saati"
                type="time"
                value={saat}
                onChange={(e) => setSaat(e.target.value)}
                yardim="İsteğe bağlı. Hiçbir hesaba girmez."
              />
            </div>
          </fieldset>

          <GorevIcerikAlanlari
            ogrenciId={ogrenciId}
            dersId={dersId}
            deger={icerik}
            onChange={setIcerik}
            hatalar={icerikHata}
          />

          <Alan
            etiket="Uzman notu"
            value={not}
            onChange={(e) => setNot(e.target.value)}
            yardim="Öğrenciye görünür."
          />

          <Buton
            type="submit"
            boyut="genis"
            yukleniyor={ekle.isPending}
            disabled={!dersId || !icerikGecerli}
          >
            Ekle
          </Buton>
        </form>
      </div>
    </div>
  );
}

function Secim({
  etiket,
  deger,
  onChange,
  devreDisi,
  children,
}: {
  etiket: string;
  deger: string;
  onChange: (v: string) => void;
  devreDisi?: boolean;
  children: React.ReactNode;
}) {
  /*
     HAM `<select>` DEĞİL `Secim` — sözleşmenin dışında kalan alan,
     sözleşmenin sağladığı hiçbir şeyi almıyor.
   
     Elle yazılan sınıflar odak halkasını taklit ediyordu ama
     `alan-bicimi.ts` sözleşmesinin geri kalanını (hata hâli, salt
     okunur hâli, `aria-describedby` bağı) taşımıyordu.
   */
  return (
    <UiSecim
      etiket={etiket}
      value={deger}
      disabled={devreDisi}
      onChange={(e) => onChange(e.target.value)}
    >
      {children}
    </UiSecim>
  );
}

/**
 * GÖREV DÜZENLEME — F-SCH
 *
 * `PATCH /program/gorevler/:id` yazılıydı, hiçbir ekran çağırmıyordu.
 * Bir görevi düzeltmenin tek yolu silip yeniden eklemekti — ve
 * öğrenci işaretlemişse görev SİLİNEMİYORDU (`OGRENCI_VERISI`), yani
 * hedefi yanlış girilmiş bir görev hiç düzeltilemiyordu.
 *
 * A8 — ders ve konu aynı öğrenci ders havuzundan değiştirilebilir.
 * Ders değişince önceki konu ve derse bağlı kaynak temizlenir.
 *
 * YALNIZCA DEĞİŞEN alan gönderiliyor: sunucu şeması "değiştirilecek
 * alan yok" diye reddediyor ve değişmemiş alanı göndermek denetim
 * kaydını da yanlış doldururdu.
 */
/**
 * Koçun yapıştırdığı bağlantıyı satırdan açabilmesi — yanlış Drive
 * dosyası yapıştırıldıysa öğrenciden önce koç görsün.
 */
function BaglantiRozeti({ url, etiket }: { url: string | null; etiket: string }) {
  return <ProgramBaglantisi url={url} etiket={etiket} />;
}

function GorevDuzenle({
  gorev,
  ogrenciId,
  onBitti,
}: {
  gorev: ProgramGorevi;
  ogrenciId: string;
  onBitti: () => void;
}) {
  const [dersId, setDersId] = useState(gorev.lessonId);
  const [konuId, setKonuId] = useState(gorev.topicId ?? "");
  const havuz = useQuery({
    queryKey: ["ders-havuzu", ogrenciId],
    queryFn: () => dersHavuzuGetir(ogrenciId),
    retry: false,
  });
  const dersler = (havuz.data?.gruplar ?? []).flatMap((g) => g.dersler);
  const ders = dersler.find((d) => d.id === dersId);
  const konular = ders ? agactakiKonular(ders.cocuklar ?? []) : [];
  const konuZorunlu = GOREV_TURLERI.find((t) => t.deger === gorev.kind)?.konuZorunlu ?? true;
  const [icerik, setIcerik] = useState<IcerikDegeri>(() => icerikGorevden(gorev));
  const [hedef, setHedef] = useState(
    gorev.targetQuestionCount === null ? "" : String(gorev.targetQuestionCount),
  );
  const [saat, setSaat] = useState(gorev.startTime ?? "");
  const [not, setNot] = useState(gorev.coachNote ?? "");

  const mut = useMutation({
    mutationFn: () => {
      const veri: {
        lessonId?: string;
        topicId?: string | null;
        targetQuestionCount?: number | null;
        startTime?: string | null;
        coachNote?: string | null;
      } & ReturnType<typeof icerikFarki> = { ...icerikFarki(gorev, icerik) };
      if (dersId !== gorev.lessonId) veri.lessonId = dersId;
      if ((konuId || null) !== gorev.topicId) veri.topicId = konuId || null;
      const yeniHedef = hedef.trim() === "" ? null : Number(hedef);
      if (yeniHedef !== gorev.targetQuestionCount) veri.targetQuestionCount = yeniHedef;
      const yeniSaat = saat.trim() === "" ? null : saat.trim();
      if (yeniSaat !== gorev.startTime) veri.startTime = yeniSaat;
      const yeniNot = not.trim() === "" ? null : not.trim();
      if (yeniNot !== gorev.coachNote) veri.coachNote = yeniNot;
      return gorevGuncelle(gorev.id, veri);
    },
    onSuccess: onBitti,
  });

  const hedefSayi = hedef.trim() === "" ? null : Number(hedef);
  const hedefGecerli = hedefSayi === null || (Number.isInteger(hedefSayi) && hedefSayi > 0);

  const icerikHata = icerikHatalari(icerik);
  const icerikGecerli = Object.keys(icerikHata).length === 0;

  const degisti =
    dersId !== gorev.lessonId ||
    (konuId || null) !== gorev.topicId ||
    Object.keys(icerikFarki(gorev, icerik)).length > 0 ||
    hedefSayi !== gorev.targetQuestionCount ||
    (saat.trim() === "" ? null : saat.trim()) !== gorev.startTime ||
    (not.trim() === "" ? null : not.trim()) !== gorev.coachNote;

  return (
    <div className="rounded-alan border-notr-200 bg-notr-0 mt-2 flex w-full min-w-0 flex-col gap-2 border p-2">
      {havuz.isError && (
        <p role="alert" className="text-hata-700 text-xs">
          Ders ve konu listesi okunamadı.
        </p>
      )}
      {havuz.isSuccess && dersler.length === 0 && (
        <p className="text-notr-700 text-xs">Öğrencinin alanında kullanılabilir ders bulunamadı.</p>
      )}
      <Secim
        etiket="Ders"
        deger={dersId}
        devreDisi={!havuz.isSuccess || dersler.length === 0}
        onChange={(v) => {
          setDersId(v);
          setKonuId("");
          setIcerik((d) => ({ ...d, kaynakId: "" }));
        }}
      >
        {!dersler.some((d) => d.id === gorev.lessonId) && (
          <option value={gorev.lessonId}>{gorev.dersAdi ?? "Mevcut ders"}</option>
        )}
        {(havuz.data?.gruplar ?? []).map((g) => (
          <optgroup key={g.alan.id} label={g.alan.ad}>
            {g.dersler.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad}
              </option>
            ))}
          </optgroup>
        ))}
      </Secim>
      <Secim
        etiket={konuZorunlu ? "Konu (zorunlu)" : "Konu (bu türde boş bırakılabilir)"}
        deger={konuId}
        onChange={setKonuId}
        devreDisi={!havuz.isSuccess || !ders}
      >
        <option value="">{konuZorunlu ? "Seçin…" : "Konu yok"}</option>
        {dersId === gorev.lessonId &&
          gorev.topicId &&
          !konular.some((k) => k.id === gorev.topicId) && (
            <option value={gorev.topicId}>{gorev.konuAdi ?? "Mevcut konu"}</option>
          )}
        {konular.map((k) => (
          <option key={k.id} value={k.id}>
            {k.ad}
          </option>
        ))}
      </Secim>
      <Alan
        etiket="Hedef soru"
        type="number"
        min={1}
        value={hedef}
        onChange={(e) => setHedef(e.target.value)}
        hata={!hedefGecerli ? "Pozitif tam sayı olmalı." : undefined}
        yardim="Boş bırakılırsa hedef tanımsız kalır — sıfır demek değil."
      />
      <Alan
        etiket="Saat"
        type="time"
        value={saat}
        onChange={(e) => setSaat(e.target.value)}
        yardim="Öğrencinin gününü düzenlemesi için; hiçbir hesaplamada kullanılmaz."
      />
      <Alan etiket="Not" value={not} onChange={(e) => setNot(e.target.value)} />
      <GorevIcerikAlanlari
        ogrenciId={ogrenciId}
        dersId={dersId}
        deger={icerik}
        onChange={setIcerik}
        hatalar={icerikHata}
      />

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-xs">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Görev güncellenemedi."}
        </p>
      )}

      <div>
        <Buton
          yukleniyor={mut.isPending}
          disabled={
            !degisti || !hedefGecerli || !icerikGecerli || !dersId || (konuZorunlu && !konuId)
          }
          onClick={() => mut.mutate()}
        >
          Kaydet
        </Buton>
      </div>
    </div>
  );
}

/**
 * REVİZYON GEÇMİŞİ — F-SCH-14
 *
 * `GET /program/:id/revizyonlar` yazılıydı, hiçbir ekran
 * çağırmıyordu: yayınlanmış bir programda yapılan her değişiklik
 * kaydediliyor ve kimse göremiyordu. "Bu görev neden değişti"
 * sorusunun cevabı vardı ama ulaşılamıyordu.
 *
 * ================================================================
 * `diff` HAM GÖSTERİLMİYOR
 * ================================================================
 * Sunucu `diff` alanını serbest bir JSON olarak döndürüyor. Ham
 * JSON'u ekrana basmak, uzmana okuyamayacağı bir şey göstermek
 * olurdu. Ekran DEĞİŞEN ALAN ADLARINI sayıyor ve gerekçeyi
 * gösteriyor; ayrıntı gerekiyorsa denetim kaydında.
 *
 * Liste ancak AÇILDIĞINDA isteniyor: program ekranı her açılışta
 * gereksiz bir sorgu daha atmasın.
 */
function Revizyonlar({ programId }: { programId: string }) {
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["program-revizyonlari", programId],
    queryFn: () => programRevizyonlari(programId),
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          Revizyon geçmişi
        </Buton>
      </div>
    );
  }

  return (
    <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-2 border p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">Revizyon geçmişi</h3>
        <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
          Kapat
        </Buton>
      </div>

      {isPending ? (
        <Iskelet satir={3} />
      ) : isError ? (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {error instanceof ApiHatasi ? error.message : "Revizyonlar alınamadı."}
        </p>
      ) : data.length === 0 ? (
        <p className="text-notr-600 text-sm">
          Bu programda revizyon yok. Revizyon ancak program YAYINLANDIKTAN sonra yapılan
          değişikliklerde oluşur.
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {data.map((r) => (
            <li key={r.id} className="rounded-alan bg-notr-50 p-3 text-sm">
              <p className="text-notr-900 font-medium">
                {r.revision}. revizyon · {anMetni(r.createdAt)}
              </p>
              {r.reason !== null && (
                <p className="text-notr-700 mt-1 whitespace-pre-wrap">{r.reason}</p>
              )}
              <p className="text-notr-600 mt-1 text-xs">{degisenAlanlar(r.diff)}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * `diff` içindeki alan adlarını sayar.
 *
 * Şekli garanti DEĞİL (sunucu `unknown` döndürüyor), o yüzden her
 * adımda kontrol ediliyor. Beklenmedik bir şekil geldiğinde çökmek
 * yerine "ayrıntı okunamadı" demek doğru: revizyonun VARLIĞI,
 * içeriğinden daha önemli bir bilgi.
 */
function degisenAlanlar(diff: unknown): string {
  if (diff === null || typeof diff !== "object") return "Değişiklik ayrıntısı okunamadı.";
  const anahtarlar = Object.keys(diff as Record<string, unknown>);
  if (anahtarlar.length === 0) return "Değişen alan kaydedilmemiş.";
  return `Değişen: ${anahtarlar.join(", ")}`;
}

/**
 * PROGRAM İÇİN ONAY TALEBİ — F-APR-01
 *
 * `POST /onaylar` ve `GET /onaylar/kayit` yazılıydı, hiçbir ekran
 * çağırmıyordu. Onay kuyruğu VARDI ve doluyordu ama kuyruğa bir şey
 * KOYMANIN yolu panelde yoktu: onay motoru tek yönlü kırıktı.
 *
 * ================================================================
 * TÜR VE KİMLİK KULLANICIYA YAZDIRILMIYOR
 * ================================================================
 * Uç `entityType` + `entityId` istiyor çünkü onay motoru bir kayda
 * değil kayıt TÜRÜNE bağlı çalışıyor. Ekranın işi doğru türü
 * göndermek; kullanıcıdan "WeeklySchedule" yazmasını istemek, iki
 * gün önce kaldırdığımız "kimlik yapıştır" kalıbının aynısı olurdu.
 *
 * ================================================================
 * AÇIK TALEP VARSA DÜĞME YOK
 * ================================================================
 * Sunucu ikinci talebi zaten açmıyor. Düğmeyi göstermek, basılıp
 * hata alınacak bir vaat olurdu; onun yerine mevcut talebin durumu
 * gösteriliyor.
 */
function OnayTalepleri({ programId, ogrenciId }: { programId: string; ogrenciId: string }) {
  const qc = useQueryClient();
  const [ozet, setOzet] = useState("");

  const liste = useQuery({
    queryKey: ["onay-talepleri", programId],
    queryFn: () => kayitOnayTalepleri("WeeklySchedule", programId),
    retry: false,
  });

  const ac = useMutation({
    mutationFn: () =>
      onayTalebiAc({
        entityType: "WeeklySchedule",
        entityId: programId,
        studentId: ogrenciId,
        summary: ozet.trim() || null,
      }),
    onSuccess: async () => {
      setOzet("");
      await qc.invalidateQueries({ queryKey: ["onay-talepleri", programId] });
      await qc.invalidateQueries({ queryKey: ["onay-bekleyenler"] });
    },
  });

  // Yetkisi olmayana hicbir sey gosterilmiyor.
  if (liste.isError) return null;
  if (liste.isPending) return null;

  const acikTalep = liste.data.find((t) => t.status === "PENDING");

  return (
    <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-2 border p-4">
      <h3 className="text-notr-900 text-sm font-semibold">Onay</h3>

      {acikTalep ? (
        <p className="text-notr-700 text-sm">
          Bu program için <strong>açık bir onay talebi</strong> var (
          {new Date(acikTalep.createdAt).toLocaleDateString("tr-TR")}). Karar verilene kadar ikinci
          talep açılamaz.
        </p>
      ) : (
        <>
          <p className="text-notr-600 text-xs">
            Yayınlamadan önce onay isteyebilirsiniz. Talep, onay kuyruğuna düşer.
          </p>
          <Alan
            etiket="Özet (isteğe bağlı)"
            value={ozet}
            onChange={(e) => setOzet(e.target.value)}
            yardim="Onaylayan kişinin neye baktığını bilmesi için."
          />
          {ac.isError && (
            <p role="alert" className="text-hata-700 text-sm font-medium">
              {ac.error instanceof ApiHatasi ? ac.error.message : "Onay talebi açılamadı."}
            </p>
          )}
          <div>
            <Buton yukleniyor={ac.isPending} onClick={() => ac.mutate()}>
              Onay iste
            </Buton>
          </div>
        </>
      )}

      {liste.data.length > 0 && (
        <ul className="text-notr-600 mt-1 flex flex-col gap-1 text-xs">
          {liste.data.map((t) => (
            <li key={t.id}>
              {new Date(t.createdAt).toLocaleDateString("tr-TR")} · {t.status}
              {t.reason !== null && ` · ${t.reason}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
